import { differenceInHours, format, isAfter, isBefore, isValid, parseISO, startOfDay } from 'date-fns';
import frLocale from 'date-fns/locale/fr';

/**
 * Merge company `booking_parameters` with per-agency rules.
 * Agency fields may live on the agency root, under `settings`, or under `booking_parameters`.
 *
 * Fleetee API (`v1/web/agencies`) exposes booking limits on each agency object, e.g.:
 * `min_days`, `min_hours` (durée min. quand la location &lt; 24 h), `min_time_before_start`,
 * `max_time_before_start`, etc. The canonical field for short-rental minimum duration is **`min_hours`**.
 */

const USE_COMPANY_KEYS = [
    'use_company_booking_parameters',
    'use_company_reservation_parameters',
    'use_booking_parameters_from_company',
];

/** Minimum duration for rentals under one day — canonical API key `min_hours`; aliases for older/other payloads. */
const MIN_HOURS_FIELD_KEYS = [
    'min_hours',
    'min_hours_under_day',
    'min_hours_under_24',
    'min_hours_under_24h',
    'min_hours_when_under_one_day',
    'minimum_hours_under_one_day',
    'minimum_duration_hours_under_24',
    'short_rental_min_hours',
];

function readUseCompanyBookingParameters(agency) {
    if (!agency) {
        return undefined;
    }
    for (const key of USE_COMPANY_KEYS) {
        const v = getAgencyField(agency, key);
        if (v !== undefined) {
            return v;
        }
    }
    return undefined;
}

function getAgencyField(agency, field) {
    if (!agency) {
        return undefined;
    }
    if (Object.prototype.hasOwnProperty.call(agency, field)) {
        return agency[field];
    }
    const st = agency.settings;
    if (st && typeof st === 'object' && Object.prototype.hasOwnProperty.call(st, field)) {
        return st[field];
    }
    const bp = agency.booking_parameters;
    if (bp && typeof bp === 'object' && Object.prototype.hasOwnProperty.call(bp, field)) {
        return bp[field];
    }
    return undefined;
}

function hasAgencyField(agency, field) {
    if (!agency) {
        return false;
    }
    if (Object.prototype.hasOwnProperty.call(agency, field)) {
        return true;
    }
    const st = agency.settings;
    if (st && typeof st === 'object' && Object.prototype.hasOwnProperty.call(st, field)) {
        return true;
    }
    const bp = agency.booking_parameters;
    if (bp && typeof bp === 'object' && Object.prototype.hasOwnProperty.call(bp, field)) {
        return true;
    }
    return false;
}

function toFiniteNumber(value, fallback) {
    if (value === null || value === undefined || value === '') {
        return fallback;
    }
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
}

function toBooleanFlag(value) {
    if (value === true || value === false) {
        return value;
    }
    if (value === 1 || value === '1') {
        return true;
    }
    if (value === 0 || value === '0') {
        return false;
    }
    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (['true', 'yes', 'on'].includes(normalized)) {
            return true;
        }
        if (['false', 'no', 'off'].includes(normalized)) {
            return false;
        }
    }
    return undefined;
}

function pickDefined(agency, field, companyVal) {
    if (!agency || !hasAgencyField(agency, field)) {
        return companyVal;
    }
    return toFiniteNumber(getAgencyField(agency, field), companyVal);
}

/**
 * First matching field wins (same layers as getAgencyField). Used for short-rental min hours.
 * @returns {number|undefined} undefined if no alias key is set on the agency.
 */
function getFirstMinHoursFromAgency(agency) {
    if (!agency) {
        return undefined;
    }
    for (const key of MIN_HOURS_FIELD_KEYS) {
        if (hasAgencyField(agency, key)) {
            return toFiniteNumber(getAgencyField(agency, key), 0);
        }
    }
    return undefined;
}

/**
 * @param {Record<string, unknown>|null|undefined} agency
 * @param {Record<string, unknown>|null|undefined} companyParams
 * @returns {{ bookingMinDays: number, bookingMaxDays: number, bookingMinHours: number, bookingMaxHours: number, bookingMinTimeBeforeStart: number, bookingMaxTimeBeforeStart: number }}
 */
export function mergeBookingParametersWithAgency(agency, companyParams) {
    const c = companyParams ?? {};
    const cMinDays = toFiniteNumber(c.min_days, 0);
    const cMaxDays = toFiniteNumber(c.max_days, 0);
    const cMinHours = toFiniteNumber(c.min_hours, 0);
    const cMaxHours = toFiniteNumber(c.max_hours, 0);
    const cMinTimeBeforeStart = toFiniteNumber(c.min_time_before_start, 3);
    const cMaxTimeBeforeStart = toFiniteNumber(c.max_time_before_start, 12);

    if (!agency) {
        return {
            bookingMinDays: cMinDays,
            bookingMaxDays: cMaxDays,
            bookingMinHours: cMinHours,
            bookingMaxHours: cMaxHours,
            bookingMinTimeBeforeStart: cMinTimeBeforeStart,
            bookingMaxTimeBeforeStart: cMaxTimeBeforeStart,
        };
    }

    const useCompany = toBooleanFlag(readUseCompanyBookingParameters(agency));

    if (useCompany === true) {
        return {
            bookingMinDays: cMinDays,
            bookingMaxDays: cMaxDays,
            bookingMinHours: cMinHours,
            bookingMaxHours: cMaxHours,
            bookingMinTimeBeforeStart: cMinTimeBeforeStart,
            bookingMaxTimeBeforeStart: cMaxTimeBeforeStart,
        };
    }

    if (useCompany === false) {
        const own = (field, whenMissing) =>
            hasAgencyField(agency, field)
                ? toFiniteNumber(getAgencyField(agency, field), whenMissing)
                : whenMissing;

        return {
            bookingMinDays: own('min_days', 0),
            bookingMaxDays: own('max_days', 0),
            bookingMinHours: (() => {
                const v = getFirstMinHoursFromAgency(agency);
                return v !== undefined ? v : 0;
            })(),
            bookingMaxHours: own('max_hours', 0),
            bookingMinTimeBeforeStart: own('min_time_before_start', 0),
            bookingMaxTimeBeforeStart: own('max_time_before_start', cMaxTimeBeforeStart),
        };
    }

    return {
        bookingMinDays: pickDefined(agency, 'min_days', cMinDays),
        bookingMaxDays: pickDefined(agency, 'max_days', cMaxDays),
        bookingMinHours: (() => {
            const v = getFirstMinHoursFromAgency(agency);
            return v !== undefined ? v : cMinHours;
        })(),
        bookingMaxHours: pickDefined(agency, 'max_hours', cMaxHours),
        bookingMinTimeBeforeStart: pickDefined(agency, 'min_time_before_start', cMinTimeBeforeStart),
        bookingMaxTimeBeforeStart: pickDefined(agency, 'max_time_before_start', cMaxTimeBeforeStart),
    };
}

export function parseRentalDate(value) {
    if (value == null || value === '') return null;
    if (value instanceof Date) return isValid(value) ? value : null;
    if (isValid(value)) return value;
    const parsed = parseISO(String(value));
    return isValid(parsed) ? parsed : null;
}

export function hasValidRentalDateRange(startDate, endDate) {
    return Boolean(parseRentalDate(startDate) && parseRentalDate(endDate));
}

/**
 * Minimum rental duration check (only when {@link hasValidRentalDateRange} is true).
 * - `min_days` → full 24h blocks (2 days = 48 hours minimum).
 * - `min_hours` → only when `min_days` is 0, for short rentals (typically &lt; 24h rule in Fleetee).
 *
 * @param {number} hourDiff signed or absolute hours between start and end
 * @param {number} minDays
 * @param {number} minHours
 * @returns {string|null} French error message or null if valid
 */
export function getMinRentalDurationError(hourDiff, minDays, minHours) {
    const hours = Math.abs(Number(hourDiff));
    if (!Number.isFinite(hours)) return null;
    const days = Number(minDays) || 0;
    const minH = Number(minHours) || 0;

    if (days > 0 && Math.ceil(hours / 24) < days) {
        return `La durée minimale de location est de ${days} jour${days > 1 ? 's' : ''}`;
    }
    if (days === 0 && minH > 0 && hours < minH) {
        return `La durée minimale de location est de ${minH} heure${minH > 1 ? 's' : ''}`;
    }
    return null;
}

export function isBelowMinRentalDuration(hourDiff, minDays, minHours) {
    return getMinRentalDurationError(hourDiff, minDays, minHours) !== null;
}

/**
 * Maximum rental duration check (mirrors {@link getMinRentalDurationError}).
 * - `max_days` → full 24h blocks.
 * - `max_hours` → only when `max_days` is 0, for short rentals.
 * - `max_days` and `max_hours` both 0 means no maximum (nothing is blocked).
 *
 * @param {number} hourDiff
 * @param {number} maxDays 0 means no maximum expressed in days
 * @param {number} maxHours 0 means no maximum expressed in hours (only used when maxDays is 0)
 * @returns {string|null}
 */
export function getMaxRentalDurationError(hourDiff, maxDays, maxHours = 0) {
    const hours = Math.abs(Number(hourDiff));
    if (!Number.isFinite(hours)) return null;
    const days = Number(maxDays) || 0;
    const maxH = Number(maxHours) || 0;

    if (days > 0 && Math.ceil(hours / 24) > days) {
        return `La durée maximale de location est de ${days} jour${days > 1 ? 's' : ''}`;
    }
    if (days === 0 && maxH > 0 && hours > maxH) {
        return `La durée maximale de location est de ${maxH} heure${maxH > 1 ? 's' : ''}`;
    }
    return null;
}

export function isAboveMaxRentalDuration(hourDiff, maxDays, maxHours) {
    return getMaxRentalDurationError(hourDiff, maxDays, maxHours) !== null;
}

/**
 * Shared rental date validation for search modal, responsive form, and booking flow.
 *
 * @returns {string[]}
 */
export function getRentalDateErrors({
    startDate,
    endDate,
    minDays = 0,
    minHours = 0,
    maxDays = 0,
    maxHours = 0,
    minStartDate,
    maxStartDate,
    maxTimeBeforeStart = 12,
    locale = frLocale,
}) {
    const start = parseRentalDate(startDate);
    const end = parseRentalDate(endDate);

    if (!hasValidRentalDateRange(start, end)) {
        return [];
    }

    const hourDiff = differenceInHours(end, start);
    const errors = [];

    const minDurationError = getMinRentalDurationError(
        hourDiff,
        minDays,
        minDays > 0 ? 0 : minHours
    );
    if (minDurationError) {
        errors.push(`${minDurationError}.`);
    }

    const maxDurationError = getMaxRentalDurationError(
        hourDiff,
        maxDays,
        maxDays > 0 ? 0 : maxHours
    );
    if (maxDurationError) {
        errors.push(maxDurationError);
    }

    if (minStartDate && isBefore(startOfDay(start), startOfDay(minStartDate))) {
        errors.push(
            `Veuillez sélectionner une date de réservation à partir du ${format(minStartDate, 'dd MMMM yyyy', { locale })} pour cette agence.`
        );
    }

    if (maxStartDate && isAfter(startOfDay(start), startOfDay(maxStartDate))) {
        errors.push(
            `Les réservations sont possibles jusqu'à ${maxTimeBeforeStart} mois à l'avance pour cette agence.`
        );
    }

    return errors;
}
